import { Head, Link, useForm } from '@inertiajs/react'
import { AuthShell, type ValuePanel } from '@/components/auth/AuthShell'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'

type Labels = {
  eyebrow: string
  title: string
  explanation: string
  email_label: string
  brewery_label: string
  brewery_ph: string
  count_label: string
  count_ph: string
  optional: string
  submit: string
  submit_processing: string
  success_message: string
  login_prompt: string
  login_cta: string
}

type Props = {
  labels: Labels
  value_panel: ValuePanel
}

export default function RegistrationClosed({ labels, value_panel }: Props) {
  const { data, setData, post, processing, errors, wasSuccessful } = useForm({
    email: '',
    brewery_name: '',
    keg_count: '',
    source: 'registration_closed',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/leads', { preserveScroll: true })
  }

  return (
    <>
      <Head title={labels.title} />
      <AuthShell valuePanel={value_panel}>
        <p className="text-xs font-extrabold uppercase tracking-[0.1em] text-flame">{labels.eyebrow}</p>
        <h1 className="mt-2 font-display text-3xl leading-tight text-ink">{labels.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-ink-soft">{labels.explanation}</p>

        {wasSuccessful ? (
          <div className="mt-6 flex items-center gap-3 rounded-xl bg-leaf-tint px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-leaf text-paper">
              <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={3}>
                <path d="M4 10l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <p className="text-sm font-semibold text-leaf-deep">{labels.success_message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <Field
              label={labels.email_label}
              name="email"
              type="email"
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              required
              autoFocus
              error={errors.email}
            />
            <Field
              label={`${labels.brewery_label} ${labels.optional}`}
              name="brewery_name"
              type="text"
              value={data.brewery_name}
              onChange={(e) => setData('brewery_name', e.target.value)}
              placeholder={labels.brewery_ph}
            />
            <Field
              label={`${labels.count_label} ${labels.optional}`}
              name="keg_count"
              type="number"
              min={1}
              value={data.keg_count}
              onChange={(e) => setData('keg_count', e.target.value)}
              placeholder={labels.count_ph}
              error={errors.keg_count}
            />
            <Button type="submit" disabled={processing} className="w-full disabled:opacity-60">
              {processing ? labels.submit_processing : labels.submit}
            </Button>
          </form>
        )}

        <p className="mt-6 text-sm text-ink-soft">
          {labels.login_prompt}{' '}
          <Link href="/session/new" className="font-bold text-flame hover:text-flame-deep">
            {labels.login_cta}
          </Link>
        </p>
      </AuthShell>
    </>
  )
}
